import React, { useEffect, useRef, useState, useCallback } from "react";
import useScoreSubmit from "../hooks/useScoreSubmit";
import ScoreboardPanel from "../components/ScoreboardPanel";

/* =========================================================
   CRASHLESS — Full Production Build
   Fixes applied:
   ✅ P1: Double-fire tap (pointerDown only, no onClick+onTouchStart mix)
   ✅ P1: Car positions scale with container (SVG viewBox coordinate system)
   ✅ P1: Level-up toast auto-dismisses after 2.5s
   ✅ P1: Collision threshold scales with car density per level
   ✅ P2: Waiting car always visually distinct (white/gold, unique shape, glow ring)
   ✅ P2: Animation speed frame-rate normalised (delta/16.67)
   ✅ P2: Render always synced (no random throttle)
   ✅ Web Audio API — crash boom, perfect chime, good tone, risky buzz
   ✅ Animated score flyer (+30 PERFECT floats and fades)
   ✅ Haptic feedback (navigator.vibrate)
   ✅ Help panel (collapsible, "?" button)
   ✅ Release point indicator (glowing arc/arrow at π/2)
   ✅ Pre-game state (cars visible, release disabled until START)
   ✅ Streak breaks on risky (design decision — now consistent + shown in help)
   ✅ uid() uses counter, not Date.now()
   ✅ Level ladder shows progress bar per level
   ✅ Game Over shows level reached
   ✅ Mobile-first typography (text-xs → lg:text-sm, text-sm → lg:text-base)
========================================================= */

/* ── Constants ── */
const TRACK_W = 560;
const CX = TRACK_W / 2;
const CY = TRACK_W / 2;
const OUTER_R = 235;
const INNER_R = 185;
const RELEASE_ANGLE = Math.PI / 2; // bottom of circle

const SAFE_GAP    = 0.28;
const GOOD_GAP    = 0.42;
const PERFECT_GAP = 0.65;

const LEVELS = [
  { level:1, name:"Safe Start",   carsTarget:8,  speed:0.010, scoreTarget:500,      color:"#22c55e", collisionThresh:0.10 },
  { level:2, name:"Warm Traffic", carsTarget:12, speed:0.014, scoreTarget:1500,     color:"#06b6d4", collisionThresh:0.09 },
  { level:3, name:"Busy Circle",  carsTarget:18, speed:0.018, scoreTarget:3500,     color:"#f59e0b", collisionThresh:0.08 },
  { level:4, name:"Rush Hour",    carsTarget:24, speed:0.024, scoreTarget:7000,     color:"#f97316", collisionThresh:0.07 },
  { level:5, name:"Chaos Mode",   carsTarget:32, speed:0.031, scoreTarget:Infinity, color:"#ef4444", collisionThresh:0.065 },
];

const TRAFFIC_COLORS = ["#22c55e","#06b6d4","#f59e0b","#ef4444","#8b5cf6","#ec4899","#14b8a6","#fb923c"];
const DEFAULT_SPONSORS = ["NIKE", "ZARA", "APPLE", "SONY", "PUMA"];
const WAITING_CAR_COLOR = "#ffffff"; // always white — never shared with traffic

/* ── Tiny helpers ── */
let _uid = 1;
const uid = () => `c${_uid++}`;

const randomTrafficColor = () => TRAFFIC_COLORS[Math.floor(Math.random() * TRAFFIC_COLORS.length)];

function getMultiplier(streak) {
  if (streak >= 35) return 3;
  if (streak >= 20) return 2.5;
  if (streak >= 10) return 2;
  if (streak >= 5)  return 1.5;
  return 1;
}

function polarToSVG(radius, angle) {
  return { x: CX + radius * Math.cos(angle), y: CY + radius * Math.sin(angle) };
}

function getLaneRadius(lane) {
  return lane === "outer" ? OUTER_R : INNER_R;
}

function buildInitialTraffic(levelCfg, sponsors) {
  const cars = [];
  const outerCount = Math.ceil(levelCfg.carsTarget * 0.55);
  const innerCount = levelCfg.carsTarget - outerCount;
  for (let i = 0; i < outerCount; i++) {
    cars.push({ id: uid(), lane:"outer", angle:(Math.PI*2*i)/outerCount, speed:levelCfg.speed,       color:randomTrafficColor(), isPlayer:false, sponsor: sponsors[Math.floor(Math.random() * sponsors.length)] });
  }
  for (let i = 0; i < innerCount; i++) {
    cars.push({ id: uid(), lane:"inner", angle:(Math.PI*2*i)/innerCount, speed:levelCfg.speed*0.92, color:randomTrafficColor(), isPlayer:false, sponsor: sponsors[Math.floor(Math.random() * sponsors.length)] });
  }
  return cars;
}

function getGapToNearest(cars, lane, angle) {
  const lane_cars = cars.filter(c => c.lane === lane);
  if (!lane_cars.length) return 999;
  let min = Infinity;
  lane_cars.forEach(c => {
    let d = Math.abs(c.angle - angle);
    if (d > Math.PI) d = Math.PI*2 - d;
    if (d < min) min = d;
  });
  return min;
}

function classifyGap(gap) {
  if (gap > PERFECT_GAP) return "perfect";
  if (gap > GOOD_GAP)    return "good";
  if (gap > SAFE_GAP)    return "risky";
  return "crash";
}

/* ── Web Audio ── */
let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return _audioCtx;
}
function playTone(freq, type, duration, gainStart, gainEnd, startDelay = 0) {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = type; osc.frequency.value = freq;
    const t = ctx.currentTime + startDelay;
    gain.gain.setValueAtTime(gainStart, t);
    gain.gain.exponentialRampToValueAtTime(Math.max(gainEnd, 0.001), t + duration);
    osc.start(t); osc.stop(t + duration + 0.05);
  } catch(e) {}
}
function soundPerfect() {
  playTone(880, "sine", 0.12, 0.4, 0.01);
  playTone(1320,"sine", 0.12, 0.3, 0.01, 0.07);
  playTone(1760,"sine", 0.15, 0.25,0.01, 0.13);
}
function soundGood() {
  playTone(660, "sine", 0.15, 0.35, 0.01);
  playTone(880, "sine", 0.12, 0.25, 0.01, 0.10);
}
function soundRisky() {
  playTone(220, "sawtooth", 0.10, 0.3, 0.01);
}
function soundCrash() {
  try {
    const ctx = getAudioCtx();
    // Noise burst
    const bufSize = ctx.sampleRate * 0.5;
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = (Math.random()*2-1) * (1 - i/bufSize);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.9, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    src.connect(gain); gain.connect(ctx.destination);
    src.start();
    // Low boom
    playTone(80, "sine", 0.4, 0.6, 0.01);
  } catch(e) {}
}
function haptic(pattern) {
  try { navigator.vibrate && navigator.vibrate(pattern); } catch(e) {}
}

/* ── Score Flyer ── */
const FLYER_COLORS = { perfect:"#a3e635", good:"#34d399", risky:"#fbbf24", crash:"#f87171" };

/* =========================================================
   MAIN COMPONENT
========================================================= */
export default function Crashless({ userName = "Player" }) {
  /* ── Scorecard/leaderboard (added) ── */
  const { isNewRecord, leaderboard, submitScore, fetchLeaderboard } = useScoreSubmit("crashlanes");
  const matchStartedAtRef = useRef(0);

  /* ── Game state ── */
  const [running,  setRunning]  = useState(false);
  const [paused,   setPaused]   = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score,    setScore]    = useState(0);
  const [streak,   setStreak]   = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [level,    setLevel]    = useState(1);
  const [levelReached, setLevelReached] = useState(1);
  const [lastType, setLastType] = useState("");
  const [showHelp, setShowHelp] = useState(false);
  const [levelToast, setLevelToast] = useState(null); // { lvl, name, color }
  const [flyers, setFlyers] = useState([]); // [{id, text, color, x, y}]
  const [sponsors, setSponsors] = useState(DEFAULT_SPONSORS);

  /* ── Refs (avoid stale closures in rAF) ── */
  const carsRef       = useRef([]);
  const runningRef    = useRef(false);
  const pausedRef     = useRef(false);
  const levelRef      = useRef(1);
  const scoreRef      = useRef(0);
  const streakRef     = useRef(0);
  const bestStreakRef = useRef(0);
  const animRef       = useRef(null);
  const lastTimeRef   = useRef(0);

  const [carsRender, setCarsRender] = useState([]);
  const [waitingCar, setWaitingCar] = useState(() => ({
    id: uid(), lane: "outer", color: WAITING_CAR_COLOR
  }));

  const currentLevel = LEVELS[level - 1];

  /* ── Derived meters ── */
  const densityPercent = Math.min(100, Math.round((carsRender.length / LEVELS[4].carsTarget) * 100));
  const speedPercent   = Math.round((currentLevel.speed / LEVELS[4].speed) * 100);
  const multiplier     = getMultiplier(streak);

  /* ── Load best streak ── */
  useEffect(() => {
    try {
      const saved = parseInt(localStorage.getItem("crashless_bestStreak") || "0", 10);
      if (saved > 0) { setBestStreak(saved); bestStreakRef.current = saved; }
    } catch(e) {}
    // init traffic
    const cars = buildInitialTraffic(LEVELS[0], sponsors);
    carsRef.current = cars;
    setCarsRender([...cars]);
  }, []);

  /* ── Sync refs ── */
  useEffect(() => { runningRef.current  = running;  }, [running]);
  useEffect(() => { pausedRef.current   = paused;   }, [paused]);
  useEffect(() => { levelRef.current    = level;    }, [level]);
  useEffect(() => { scoreRef.current    = score;    }, [score]);
  useEffect(() => { streakRef.current   = streak;   }, [streak]);

  /* ── Score flyer helper ── */
  const spawnFlyer = useCallback((text, quality) => {
    // center of track area — approximate
    const id = uid();
    const x = 50 + Math.random() * 30; // % across
    const y = 45 + Math.random() * 10;
    setFlyers(prev => [...prev, { id, text, color: FLYER_COLORS[quality] || "#fff", x, y }]);
    setTimeout(() => setFlyers(prev => prev.filter(f => f.id !== id)), 1200);
  }, []);

  /* ── Level up logic ── */
  const triggerLevelUp = useCallback((newLevel) => {
    const cfg = LEVELS[newLevel - 1];
    setLevel(newLevel);
    setLevelReached(prev => Math.max(prev, newLevel));
    levelRef.current = newLevel;
    setLevelToast({ lvl: newLevel, name: cfg.name, color: cfg.color });
    setTimeout(() => setLevelToast(null), 2500);
    // Upgrade speeds
    carsRef.current = carsRef.current.map(c => ({
      ...c,
      speed: c.lane === "outer" ? cfg.speed : cfg.speed * 0.92
    }));
  }, []);

  /* ── Watch score for level up ── */
  useEffect(() => {
    if (level < 5 && score >= LEVELS[level - 1].scoreTarget) {
      triggerLevelUp(level + 1);
    }
  }, [score, level, triggerLevelUp]);

  /* ── Auto-density growth ── */
  useEffect(() => {
    if (!running || paused) return;
    const cfg = LEVELS[levelRef.current - 1];
    const interval = setInterval(() => {
      if (carsRef.current.length >= cfg.carsTarget) return;
      const lane = Math.random() > 0.5 ? "outer" : "inner";
      // Spawn away from release point to avoid instant collision
      const safeAngle = (RELEASE_ANGLE + Math.PI + (Math.random()-0.5)*1.5) % (Math.PI*2);
      carsRef.current.push({
        id: uid(), lane, angle: safeAngle,
        speed: lane === "outer" ? cfg.speed : cfg.speed*0.92,
        color: randomTrafficColor(), isPlayer: false,
        sponsor: sponsors[Math.floor(Math.random() * sponsors.length)]
      });
      setCarsRender([...carsRef.current]);
    }, 4000);
    return () => clearInterval(interval);
  }, [running, paused, level]);

  /* ── Collision check (level-aware threshold) ── */
  const checkCollisions = useCallback((cars) => {
    const cfg = LEVELS[levelRef.current - 1];
    const thresh = cfg.collisionThresh;
    for (const lane of ["outer","inner"]) {
      const lc = cars.filter(c => c.lane === lane);
      for (let i = 0; i < lc.length; i++) {
        for (let j = i+1; j < lc.length; j++) {
          let d = Math.abs(lc[i].angle - lc[j].angle);
          if (d > Math.PI) d = Math.PI*2 - d;
          if (d < thresh) return true;
        }
      }
    }
    return false;
  }, []);

  /* ── End game ── */
  const endGame = useCallback(() => {
    runningRef.current = false;
    setRunning(false);
    setGameOver(true);
    setPaused(false);
    soundCrash();
    haptic([50,30,100,30,200]);
    if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; }

    const timeTakenSec = Math.round((Date.now() - matchStartedAtRef.current) / 1000);
    submitScore({ score: scoreRef.current, timeTakenSec, levelReached: levelRef.current });
  }, [submitScore]);

  /* ── Main animation loop (normalised timestep) ── */
  useEffect(() => {
    if (!running || paused) {
      if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; }
      return;
    }
    const animate = (now) => {
      if (!runningRef.current || pausedRef.current) return;
      const raw = now - lastTimeRef.current;
      lastTimeRef.current = now;
      const delta = Math.min(raw, 50) / 16.667; // normalised to 60fps

      const updated = carsRef.current.map(c => {
        let a = c.angle + c.speed * delta;
        if (a > Math.PI*2) a -= Math.PI*2;
        return { ...c, angle: a };
      });

      if (checkCollisions(updated)) { endGame(); return; }

      carsRef.current = updated;
      setCarsRender([...updated]);
      animRef.current = requestAnimationFrame(animate);
    };

    lastTimeRef.current = performance.now();
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [running, paused, checkCollisions, endGame]);

  /* ── Start / Pause / Reset ── */
  const startGame = useCallback(() => {
    if (gameOver || running) return;
    matchStartedAtRef.current = Date.now();
    setRunning(true); runningRef.current = true;
    setPaused(false);  pausedRef.current = false;
  }, [gameOver, running]);

  const pauseGame = useCallback(() => {
    if (!running || gameOver) return;
    setPaused(p => { pausedRef.current = !p; return !p; });
  }, [running, gameOver]);

  const resetGame = useCallback(() => {
    if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; }
    const cfg = LEVELS[0];
    setLevel(1); levelRef.current = 1;
    setScore(0); scoreRef.current = 0;
    setStreak(0); streakRef.current = 0;
    setLevelReached(1);
    setGameOver(false);
    setRunning(false);  runningRef.current = false;
    setPaused(false);   pausedRef.current = false;
    setLastType("");
    setLevelToast(null);
    setFlyers([]);
    const cars = buildInitialTraffic(cfg, sponsors);
    carsRef.current = cars;
    setCarsRender([...cars]);
    setWaitingCar({ id: uid(), lane: Math.random()>0.5?"outer":"inner", color: WAITING_CAR_COLOR });
  }, []);

  /* ── Release car ── */
  const releaseWaitingCar = useCallback(() => {
    if (!runningRef.current || pausedRef.current || gameOver) return;

    const gap     = getGapToNearest(carsRef.current, waitingCar.lane, RELEASE_ANGLE);
    const quality = classifyGap(gap);
    setLastType(quality);

    if (quality === "crash") {
      soundCrash(); haptic([80,40,200]);
      endGame(); return;
    }

    // Points
    let pts = 0, streakGain = 0;
    if (quality === "perfect") { pts = 15; streakGain = 1; soundPerfect(); haptic([20]); }
    else if (quality === "good") { pts = 10; streakGain = 1; soundGood();   haptic([15]); }
    else { pts = 5; streakGain = -Math.min(streakRef.current, 2); soundRisky(); haptic([40,20,40]); } // risky breaks streak a bit

    const newStreak = Math.max(0, streakRef.current + streakGain);
    const mult      = getMultiplier(newStreak);
    const final     = Math.floor(pts * mult);

    setStreak(newStreak); streakRef.current = newStreak;
    if (newStreak > bestStreakRef.current) {
      bestStreakRef.current = newStreak;
      setBestStreak(newStreak);
      try { localStorage.setItem("crashless_bestStreak", newStreak.toString()); } catch(e){}
    }
    setScore(prev => { const s = prev + final; scoreRef.current = s; return s; });

    // Flyer
    spawnFlyer(`+${final} ${quality.toUpperCase()}`, quality);

    // Add released car to traffic
    const newCar = { id: waitingCar.id, lane: waitingCar.lane, angle: RELEASE_ANGLE,
                     speed: currentLevel.speed, color: randomTrafficColor(), isPlayer: false,
                     sponsor: sponsors[Math.floor(Math.random() * sponsors.length)] };
    carsRef.current.push(newCar);
    setCarsRender([...carsRef.current]);

    setWaitingCar({ id: uid(), lane: Math.random()>0.5?"outer":"inner", color: WAITING_CAR_COLOR });
  }, [gameOver, waitingCar, currentLevel, endGame, spawnFlyer]);

  /* ── Single pointer handler (no double-fire) ── */
  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    if (!running && !gameOver) { startGame(); return; }
    releaseWaitingCar();
  }, [running, gameOver, startGame, releaseWaitingCar]);

  /* ── Keyboard ── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === " " || e.key === "Enter") { e.preventDefault(); releaseWaitingCar(); }
      if (e.key.toLowerCase() === "p") pauseGame();
      if (e.key.toLowerCase() === "r") resetGame();
      if (e.key.toLowerCase() === "s") startGame();
      if (e.key.toLowerCase() === "h") setShowHelp(v => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [releaseWaitingCar, pauseGame, resetGame, startGame]);

  /* ── SVG rendering helpers ── */
  const waitingRadius = getLaneRadius(waitingCar.lane);
  const waitingPos    = polarToSVG(waitingRadius, RELEASE_ANGLE);

  // Release point glow spots per lane
  const outerRelPt = polarToSVG(OUTER_R, RELEASE_ANGLE);
  const innerRelPt = polarToSVG(INNER_R, RELEASE_ANGLE);

  /* ── Quality badge colour ── */
  const qualityColor = { perfect:"#a3e635", good:"#34d399", risky:"#fbbf24", crash:"#ef4444" }[lastType] || "#94a3b8";

  return (
    <div className="min-h-screen bg-[#050816] text-white select-none pb-6 border-b-2 border-gray-300 rounded-xl"
         style={{ fontFamily:"system-ui,-apple-system,sans-serif" }}>

      {/* ── SCORE FLYERS (fixed overlay) ── */}
      {flyers.map(f => (
        <div key={f.id}
          className="fixed pointer-events-none z-[200] font-black text-sm lg:text-xl"
          style={{
            left:`${f.x}%`, top:`${f.y}%`,
            color: f.color,
            textShadow:`0 0 12px ${f.color}`,
            animation:"flyup 1.2s ease-out forwards",
          }}>
          {f.text}
        </div>
      ))}

      {/* ── LEVEL-UP TOAST ── */}
      {levelToast && (
        <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[150] pointer-events-none"
             style={{ animation:"toastIn 0.3s ease-out" }}>
          <div className="px-5 py-2 rounded-xl border backdrop-blur-md text-center"
               style={{ background:"rgba(15,23,42,0.95)", borderColor: levelToast.color+"66" }}>
            <div className="text-xs text-gray-200 uppercase tracking-widest">Level Up!</div>
            <div className="text-sm lg:text-base font-black" style={{ color: levelToast.color }}>
              Level {levelToast.lvl} — {levelToast.name}
            </div>
          </div>
        </div>
      )}

      {/* ── HELP MODAL ── */}
      {showHelp && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start pt-16 justify-center p-4"
             onClick={() => setShowHelp(false)}>
          <div className="bg-[#0f172a] border border-cyan-500/30 rounded-2xl p-5 w-full h-full overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-sm lg:text-base font-bold text-cyan-200">How to Play</h2>
              <button onClick={() => setShowHelp(false)}
                      className="w-fit px-2 py-1 text-gray-200 hover:text-white text-lg leading-none">✕</button>
            </div>

            <div className="space-y-3 text-xs lg:text-sm">
              <p className="text-gray-100">
                Crashless is a traffic timing game. Cars circle two rings. Your white car waits at the bottom — release it into a gap 
                without causing a collision.
              </p>

              <h2 className="text-cyan-200 text-xs lg:text-sm font-semibold pt-1">Release quality</h2>
              <ul className="space-y-1 text-gray-100">
                <li className="text-gray-200"><span className="text-green-400 font-bold">PERFECT</span> — big gap · +15 pts · streak +1</li>
                <li className="text-gray-200"><span className="text-emerald-400 font-bold">GOOD</span> — decent gap · +10 pts · streak +1</li>
                <li className="text-gray-200"><span className="text-yellow-400 font-bold">RISKY</span> — tight gap · +5 pts · streak −2</li>
                <li className="text-gray-200"><span className="text-red-400 font-bold">CRASH</span> — no gap · game over</li>
              </ul>

              <h2 className="text-cyan-200 text-xs lg:text-sm font-semibold pt-1">Levels</h2>
              <ul className="space-y-1 text-gray-100">
                    <li className="text-gray-200">There are <span className="text-white font-bold">5 levels</span> — score drives progression</li>
                    <li className="text-gray-200">Level 2 unlocks at <span className="text-cyan-300">500 pts</span></li>
                    <li className="text-gray-200">Level 3 at <span className="text-cyan-300">1,500</span> · Level 4 at <span className="text-cyan-300">3,500</span></li>
                    <li className="text-gray-200">Level 5 (Chaos) at <span className="text-cyan-300">7,000</span> — no exit</li>
                    <li className="text-gray-200">Each level adds more cars and increases speed</li>
             </ul>

              <h2 className="text-cyan-200 text-xs lg:text-sm font-semibold pt-1">Streak multiplier</h2>
              <ul className="space-y-1 text-gray-100">
                <li className="text-gray-200">5 streak → <span className="text-lime-400">×1.5</span></li>
                <li className="text-gray-200">10 streak → <span className="text-lime-400">×2.0</span></li>
                <li className="text-gray-200">20 streak → <span className="text-lime-400">×2.5</span></li>
                <li className="text-gray-200">35 streak → <span className="text-lime-400">×3.0</span></li>
              </ul>

              <h2 className="text-cyan-200 text-xs lg:text-sm font-semibold pt-1">Controls</h2>
              <ul className="space-y-1 text-gray-100">
                <li className="text-gray-200"><span className="text-gray-100">Tap / Click</span> — release car</li>
                <li className="text-gray-200"><span className="text-gray-100">Space / Enter</span> — release (keyboard)</li>
                <li className="text-gray-200"><span className="text-gray-100">P</span> — pause · <span className="text-gray-100">R</span> — restart</li>
              </ul>

              <h2 className="text-cyan-200 text-xs lg:text-sm font-semibold pt-1">Tips</h2>
              <ul className="space-y-1 text-gray-100">
                <li className="text-gray-200">Watch the <span className="text-cyan-300">glowing dot</span> at the bottom of each ring — that's the release point.</li>
                <li className="text-gray-200">The outer ring moves faster than the inner ring.</li>
                <li className="text-gray-200">Traffic density grows every 4 seconds — don't wait too long.</li>
                <li className="text-gray-200">Risky releases shave 2 off your streak — stay clean for big multipliers.</li>
              </ul>
            </div>

            <button onClick={() => setShowHelp(false)}
                    className="mt-5 w-full py-2 rounded-xl bg-cyan-500/20 border border-cyan-400 text-cyan-200 text-xs lg:text-sm font-bold">
              Got it
            </button>
          </div>
        </div>
      )}

      {/* ── HEADER CARDS ── */}
      <div className="max-w-[1600px] mx-auto px-3 pt-3">
        <div className="grid grid-cols-5 gap-2">
          {/* Player */}
          <div className="col-span-2 lg:col-span-1 bg-[#0f172a] rounded-xl border border-cyan-500/20 p-2 lg:p-4">
            <div className="text-gray-200 text-[10px] lg:text-xs uppercase tracking-wider">Player</div>
            <div className="text-xs lg:text-sm font-bold mt-1 truncate">{userName}</div>
            <div className="text-cyan-400 text-[10px] lg:text-xs mt-1">Crashless</div>
          </div>
          {/* Score */}
          <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-2 lg:p-4">
            <div className="text-gray-200 text-[10px] lg:text-xs uppercase tracking-wider">Score</div>
            <div className="text-sm lg:text-xl font-black mt-1 tabular-nums">{score}</div>
          </div>
          {/* Streak */}
          <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-2 lg:p-4">
            <div className="text-gray-200 text-[10px] lg:text-xs uppercase tracking-wider">Streak</div>
            <div className="text-sm lg:text-xl font-black mt-1 text-lime-400 tabular-nums">{streak}</div>
            <div className="text-[10px] lg:text-xs text-gray-200">×{multiplier}</div>
          </div>
          {/* Best */}
          <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-2 lg:p-4">
            <div className="text-gray-200 text-[10px] lg:text-xs uppercase tracking-wider">Best</div>
            <div className="text-sm lg:text-xl font-black mt-1 text-yellow-400 tabular-nums">{bestStreak}</div>
          </div>
          {/* Level + Help */}
          <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-2 lg:p-4 relative">
            <button onClick={() => setShowHelp(true)}
                    className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-[9px] font-bold flex items-center justify-center"
                    aria-label="Help">?</button>
            <div className="text-gray-200 text-[10px] lg:text-xs uppercase tracking-wider">Level</div>
            <div className="text-sm lg:text-xl font-black mt-1">{level}</div>
            <div className="text-[10px] lg:text-xs font-medium truncate" style={{ color: currentLevel.color }}>
              {currentLevel.name}
            </div>
          </div>
        </div>

        {/* ── MAIN AREA ── */}
        <div className="mt-3 grid lg:grid-cols-[220px_1fr_220px] gap-3">

          {/* ── LEFT PANEL ── */}
          <div className="hidden lg:flex flex-col gap-3">
            {/* Speed */}
            <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-4">
              <div className="text-gray-200 text-xs uppercase tracking-wider">Speed</div>
              <div className="text-lg font-black mt-1 text-cyan-400">{speedPercent}%</div>
              <div className="w-full bg-slate-800 h-2 rounded-full mt-2">
                <div className="h-2 rounded-full bg-cyan-400 transition-all duration-500"
                     style={{ width:`${speedPercent}%` }} />
              </div>
            </div>
            {/* Density */}
            <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-4">
              <div className="text-gray-200 text-xs uppercase tracking-wider">Density</div>
              <div className="text-lg font-black mt-1 text-lime-400">{densityPercent}%</div>
              <div className="w-full bg-slate-800 h-2 rounded-full mt-2">
                <div className="h-2 rounded-full bg-lime-400 transition-all duration-500"
                     style={{ width:`${densityPercent}%` }} />
              </div>
            </div>
            {/* Next level */}
            <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-4">
              <div className="text-gray-200 text-xs uppercase tracking-wider">Next Level</div>
              <div className="text-lg font-black mt-1">
                {currentLevel.scoreTarget === Infinity ? "MAX" : currentLevel.scoreTarget}
              </div>
              {currentLevel.scoreTarget !== Infinity && (
                <>
                  <div className="w-full bg-slate-800 h-2 rounded-full mt-2">
                    <div className="h-2 rounded-full transition-all duration-300"
                         style={{ width:`${Math.min(100,(score/(currentLevel.scoreTarget||1))*100)}%`,
                                  background: currentLevel.color }} />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{score} / {currentLevel.scoreTarget}</div>
                </>
              )}
            </div>
            {/* Release quality last */}
            {lastType && (
              <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-4 text-center">
                <div className="text-gray-200 text-xs uppercase tracking-wider mb-1">Last</div>
                <div className="text-base font-black uppercase" style={{ color: qualityColor }}>
                  {lastType}
                </div>
              </div>
            )}
          </div>

          {/* ── TRACK (SVG-based, scales perfectly) ── */}
          <div className="flex justify-center items-center">
            <div className="relative w-full"
                 style={{ maxWidth:"min(94vw, 560px)", aspectRatio:"1/1" }}>

              {/* SVG track — coordinate system is always 560×560, scales to container */}
              <svg
                viewBox={`0 0 ${TRACK_W} ${TRACK_W}`}
                width="100%" height="100%"
                style={{ touchAction:"none", cursor: running ? "pointer" : "default" }}
                onPointerDown={handlePointerDown}
              >
                {/* ── Outer road ── */}
                <circle cx={CX} cy={CY} r={OUTER_R} fill="none"
                        stroke="#22d3ee" strokeWidth="22"
                        style={{ filter:"drop-shadow(0 0 14px rgba(34,211,238,0.7))" }} />

                {/* ── Inner road ── */}
                <circle cx={CX} cy={CY} r={INNER_R} fill="none"
                        stroke="#67e8f9" strokeWidth="18"
                        style={{ filter:"drop-shadow(0 0 8px rgba(103,232,249,0.5))" }} />

                {/* ── Center panel ── */}
                <circle cx={CX} cy={CY} r={148} fill="#050816" stroke="#22d3ee" strokeWidth="0.5" strokeOpacity="0.3" />

                {/* Center text */}
                <text x={CX} y={CY-24} textAnchor="middle" fill="white"
                      fontSize="22" fontWeight="900" letterSpacing="-1">CRASHLESS</text>
                <text x={CX} y={CY+4} textAnchor="middle" fill="#94a3b8" fontSize="12">
                  {!running && !gameOver ? "Tap to start" : "Release at the gap"}
                </text>

                {/* Last release quality in center */}
                {lastType && (
                  <text x={CX} y={CY+28} textAnchor="middle" fontSize="14" fontWeight="800"
                        fill={qualityColor} style={{ textTransform:"uppercase" }}>
                    {lastType.toUpperCase()}
                  </text>
                )}

                {/* ── Release point indicators (glowing dots per lane) ── */}
                {/* Outer release dot */}
                <circle cx={outerRelPt.x} cy={outerRelPt.y} r={12}
                        fill="none" stroke="#facc15" strokeWidth="2" strokeOpacity="0.6" />
                <circle cx={outerRelPt.x} cy={outerRelPt.y} r={5} fill="#facc15" fillOpacity="0.9"
                        style={{ filter:"drop-shadow(0 0 6px #facc15)" }} />
                {/* Inner release dot */}
                <circle cx={innerRelPt.x} cy={innerRelPt.y} r={10}
                        fill="none" stroke="#facc15" strokeWidth="2" strokeOpacity="0.6" />
                <circle cx={innerRelPt.x} cy={innerRelPt.y} r={4} fill="#facc15" fillOpacity="0.9"
                        style={{ filter:"drop-shadow(0 0 6px #facc15)" }} />

                {/* ── Lane labels (12 o'clock) ── */}
                <text x={CX} y={CY-OUTER_R+8} textAnchor="middle" fill="#22d3ee" fontSize="9" fontWeight="600">OUTER</text>
                <text x={CX} y={CY-INNER_R+8} textAnchor="middle" fill="#67e8f9" fontSize="9" fontWeight="600">INNER</text>

                {/* ── Traffic cars ── */}
                {carsRender.map(car => {
                    const r   = getLaneRadius(car.lane);
                    const pos = polarToSVG(r, car.angle);
                    const rot = car.angle * 57.2958 + 90;
                    return (
                        <g key={car.id} transform={`translate(${pos.x},${pos.y}) rotate(${rot})`}>
                        {/* Car body */}
                        <rect x={-6} y={-11} width={12} height={22} rx={3}
                                fill={car.color} stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
                        {/* Windscreen top */}
                        <rect x={-4} y={-9} width={8} height={5} rx={1} fill="rgba(255,255,255,0.25)" />
                        {/* Windscreen bottom */}
                        <rect x={-4} y={3}  width={8} height={5} rx={1} fill="rgba(255,255,255,0.25)" />
                        {/* Sponsor — body panel between windscreens */}
                        <text x={0} y={-1} textAnchor="middle" fontSize="4" fontWeight="700"
                                fill="rgba(255,255,255,0.9)" transform="rotate(-90)" letterSpacing="-0.3">
                            {car.sponsor}
                        </text>
                        </g>
                    );
                    })}

                    {/* ── Waiting car (always white, distinctive shape with arrow) ── */}
                    {(() => {
                    const pos = polarToSVG(waitingRadius, RELEASE_ANGLE);
                    const rot = RELEASE_ANGLE * 57.2958 + 90;
                    return (
                        <g transform={`translate(${pos.x},${pos.y}) rotate(${rot})`}>
                        {/* Glow ring */}
                        <circle cx={0} cy={0} r={18} fill="none" stroke="#facc15"
                                strokeWidth="2" strokeOpacity="0.5"
                                style={{ animation: running ? "pulseRing 1s ease-in-out infinite" : "none" }} />
                        {/* Car body — white, larger */}
                        <rect x={-7} y={-13} width={14} height={26} rx={4}
                                fill="white" stroke="#facc15" strokeWidth="2" />
                        {/* Windscreen */}
                        <rect x={-5} y={-11} width={10} height={6} rx={1.5} fill="#fef08a" />
                        {/* Arrow indicator on roof */}
                        <polygon points="0,-18 -4,-13 4,-13" fill="#facc15" />
                        </g>
                    );
                    })()}

                {/* ── Pre-game overlay ── */}
                {!running && !gameOver && (
                  <g>
                    <circle cx={CX} cy={CY} r={145} fill="rgba(5,8,22,0.55)" />
                    <text x={CX} y={CY-10} textAnchor="middle" fill="#22d3ee"
                          fontSize="16" fontWeight="800">TAP TO START</text>
                    <text x={CX} y={CY+14} textAnchor="middle" fill="#94a3b8" fontSize="11">
                      or press S
                    </text>
                  </g>
                )}

                {/* ── Paused overlay ── */}
                {paused && (
                  <g>
                    <circle cx={CX} cy={CY} r={145} fill="rgba(5,8,22,0.7)" />
                    <text x={CX} y={CY+6} textAnchor="middle" fill="#fbbf24"
                          fontSize="20" fontWeight="900">PAUSED</text>
                  </g>
                )}
              </svg>
            </div>
          </div>

          {/* ── RIGHT PANEL — Level Ladder ── */}
          <div className="hidden lg:block">
            <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-4">
              <div className="text-gray-200 text-xs uppercase tracking-wider mb-3">Level Ladder</div>
              <div className="space-y-2">
                {LEVELS.map(l => {
                  const isActive  = level === l.level;
                  const isDone    = levelReached > l.level;
                  const progress  = isDone ? 100 : isActive
                    ? Math.min(100, Math.round((score / (l.scoreTarget||1))*100))
                    : 0;
                  return (
                    <div key={l.level}
                         className={`p-2.5 rounded-xl border transition-colors ${
                           isActive ? "border-cyan-400 bg-cyan-500/10" : isDone ? "border-green-500/30 bg-green-500/5" : "border-transparent bg-slate-900"
                         }`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-xs text-gray-300 font-medium">Level {l.level}</div>
                          <div className="text-xs font-semibold" style={{ color: l.color }}>{l.name}</div>
                        </div>
                        {isDone && <span className="text-green-400 text-xs">✓</span>}
                        {isActive && <span className="text-cyan-400 text-xs">▶</span>}
                      </div>
                      {(isActive || isDone) && (
                        <div className="w-full bg-slate-800 h-1 rounded-full mt-1.5">
                          <div className="h-1 rounded-full transition-all duration-500"
                               style={{ width:`${progress}%`, background: l.color }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ── MOBILE METERS (below track, visible on mobile only) ── */}
        <div className="lg:hidden mt-2 grid grid-cols-3 gap-2">
          <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-2">
            <div className="text-gray-200 text-[9px] uppercase tracking-wider">Speed</div>
            <div className="text-xs font-bold text-cyan-400 mt-0.5">{speedPercent}%</div>
            <div className="w-full bg-slate-800 h-1.5 rounded-full mt-1">
              <div className="h-1.5 rounded-full bg-cyan-400" style={{ width:`${speedPercent}%` }} />
            </div>
          </div>
          <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-2">
            <div className="text-gray-200 text-[9px] uppercase tracking-wider">Density</div>
            <div className="text-xs font-bold text-lime-400 mt-0.5">{densityPercent}%</div>
            <div className="w-full bg-slate-800 h-1.5 rounded-full mt-1">
              <div className="h-1.5 rounded-full bg-lime-400" style={{ width:`${densityPercent}%` }} />
            </div>
          </div>
          <div className="bg-[#0f172a] rounded-xl border border-cyan-500/20 p-2">
            <div className="text-gray-200 text-[9px] uppercase tracking-wider">Next Lv</div>
            <div className="text-xs font-bold mt-0.5">
              {currentLevel.scoreTarget === Infinity ? "MAX" : currentLevel.scoreTarget}
            </div>
            {currentLevel.scoreTarget !== Infinity && (
              <div className="w-full bg-slate-800 h-1.5 rounded-full mt-1">
                <div className="h-1.5 rounded-full transition-all"
                     style={{ width:`${Math.min(100,(score/(currentLevel.scoreTarget||1))*100)}%`,
                              background: currentLevel.color }} />
              </div>
            )}
          </div>
        </div>

        {/* ── MOBILE LEVEL LADDER (compact horizontal) ── */}
        <div className="lg:hidden mt-2 flex gap-1.5 overflow-x-auto pb-1">
          {LEVELS.map(l => {
            const isActive = level === l.level;
            const isDone   = levelReached > l.level;
            return (
              <div key={l.level}
                   className={`flex-shrink-0 px-2 py-1.5 rounded-lg border text-center min-w-[60px] ${
                     isActive ? "border-cyan-400 bg-cyan-500/10" : isDone ? "border-green-500/30 bg-green-500/5" : "border-transparent bg-slate-900"
                   }`}>
                <div className="text-[9px] text-gray-200">Lv {l.level}</div>
                <div className="text-[9px] font-semibold" style={{ color: l.color }}>{l.name}</div>
                {isDone && <div className="text-green-400 text-[9px]">✓</div>}
              </div>
            );
          })}
        </div>

        {/* ── CONTROL BUTTONS ── */}
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button onPointerDown={(e) => { e.preventDefault(); startGame(); }}
                  disabled={running || gameOver}
                  className="py-3 lg:py-4 rounded-xl bg-cyan-500/20 border border-cyan-400 font-bold text-xs lg:text-sm active:scale-95 transition-transform disabled:opacity-40">
            START
          </button>
          <button onPointerDown={(e) => { e.preventDefault(); pauseGame(); }}
                  disabled={!running || gameOver}
                  className="py-3 lg:py-4 rounded-xl bg-yellow-500/20 border border-yellow-400 font-bold text-xs lg:text-sm active:scale-95 transition-transform disabled:opacity-40">
            {paused ? "RESUME" : "PAUSE"}
          </button>
          <button onPointerDown={(e) => { e.preventDefault(); resetGame(); }}
                  className="py-3 lg:py-4 rounded-xl bg-red-500/20 border border-red-400 font-bold text-xs lg:text-sm active:scale-95 transition-transform">
            RESTART
          </button>
        </div>

        {/* ── BIG RELEASE BUTTON ── */}
        <button
          onPointerDown={handlePointerDown}
          className={`w-full mt-2 py-5 lg:py-6 rounded-2xl font-black text-base lg:text-lg transition-all active:scale-95 border-2 ${
            running && !paused
              ? "bg-lime-500/20 border-lime-400 text-lime-300 hover:bg-lime-500/30 shadow-[0_0_24px_rgba(163,230,77,0.4)]"
              : "bg-slate-800/50 border-slate-600 text-gray-200"
          }`}
          style={{ touchAction:"none" }}>
          {running && !paused
            ? `TAP TO RELEASE  ·  ${waitingCar.lane.toUpperCase()} LANE`
            : paused ? "PAUSED — RESUME FIRST" : "START GAME FIRST"}
        </button>

        {/* ── LAST QUALITY MOBILE ── */}
        {lastType && (
          <div className="mt-2 text-center">
            <span className="text-xs lg:text-sm font-black uppercase tracking-widest"
                  style={{ color: qualityColor }}>
              {lastType}
            </span>
          </div>
        )}
      </div>

      {/* ── GAME OVER MODAL ── */}
      {gameOver && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center z-[90] p-4">
          <div className="bg-[#0f172a] border border-red-500/40 rounded-2xl p-6 w-full max-w-sm">
            <div className="text-center mb-5">
              <div className="text-4xl mb-2">💥</div>
              <h2 className="text-lg lg:text-xl font-black text-white">GAME OVER</h2>
              <p className="text-xs lg:text-sm text-gray-200 mt-1">Traffic collision detected</p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-slate-900 rounded-xl p-3 text-center">
                <div className="text-gray-200 text-[10px] uppercase tracking-wider">Score</div>
                <div className="text-lg font-black text-white mt-1">{score}</div>
              </div>
              <div className="bg-slate-900 rounded-xl p-3 text-center">
                <div className="text-gray-200 text-[10px] uppercase tracking-wider">Best Streak</div>
                <div className="text-lg font-black text-yellow-400 mt-1">{bestStreak}</div>
              </div>
              <div className="bg-slate-900 rounded-xl p-3 text-center">
                <div className="text-gray-200 text-[10px] uppercase tracking-wider">Level Reached</div>
                <div className="text-lg font-black mt-1" style={{ color: LEVELS[levelReached-1].color }}>
                  {levelReached}
                </div>
              </div>
              <div className="bg-slate-900 rounded-xl p-3 text-center">
                <div className="text-gray-200 text-[10px] uppercase tracking-wider">Peak ×</div>
                <div className="text-lg font-black text-lime-400 mt-1">×{getMultiplier(bestStreak)}</div>
              </div>
            </div>

            <ScoreboardPanel
              score={score}
              isNewRecord={isNewRecord}
              leaderboard={leaderboard}
              fetchLeaderboard={fetchLeaderboard}
            />

            <div className="grid grid-cols-2 gap-3 mt-4">
              <button onPointerDown={(e) => { e.preventDefault(); resetGame(); }}
                      className="py-3 rounded-xl bg-cyan-500/20 border border-cyan-400 font-bold text-xs lg:text-sm text-cyan-200 active:scale-95">
                PLAY AGAIN
              </button>
              <button onPointerDown={(e) => { e.preventDefault(); setGameOver(false); setRunning(false); }}
                      className="py-3 rounded-xl bg-red-500/20 border border-red-400 font-bold text-xs lg:text-sm text-red-300 active:scale-95">
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── GLOBAL KEYFRAME ANIMATIONS ── */}
      <style>{`
        @keyframes flyup {
          0%   { transform: translateY(0)   scale(1);   opacity: 1; }
          60%  { transform: translateY(-48px) scale(1.1); opacity: 1; }
          100% { transform: translateY(-90px) scale(0.9); opacity: 0; }
        }
        @keyframes toastIn {
          from { opacity:0; transform: translateX(-50%) translateY(-8px); }
          to   { opacity:1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes pulseRing {
          0%,100% { stroke-opacity: 0.5; r: 18; }
          50%      { stroke-opacity: 1;   r: 20; }
        }
      `}</style>
    </div>
  );
}
